import React, { useState, useEffect } from 'react';
import './ReferralTree.css';

const ReferralTree = () => {
  const [entries, setEntries] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [referralData, setReferralData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalClients, setTotalClients] = useState(0);

  // API endpoint for referral tree data
  const API_URL = 'https://server.account.majestyfx.com/admin_referraltree.php';

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Make actual API call to fetch referral tree data
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Add any authentication headers if required
          // 'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setReferralData(data.data || []);
        setTotalClients(data.total_clients || 0);
      } else {
        setError(data.message || 'Failed to fetch referral data');
      }
    } catch (err) {
      setError('Error fetching referral data: ' + err.message);
      console.error('Referral tree API error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredData = referralData.filter(client => 
    client.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.client_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.parent && client.parent.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get active IB levels for a client
  const getActiveIBs = (ibs) => {
    const activeIBs = [];
    for (let i = 1; i <= 20; i++) {
      const ibKey = `ib${i}`;
      if (ibs[ibKey]) {
        activeIBs.push({ level: i, email: ibs[ibKey] });
      }
    }
    return activeIBs;
  };

  // Render referral table
  const renderReferralTable = () => {
    const displayData = filteredData.slice(0, entries);
    
    return (
      <div className="referral-table-container">
        <table className="referral-table">
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Email</th>
              <th>Trade IDs</th>
              <th>Parent</th>
              <th>Status</th>
              <th>IB Hierarchy</th>
            </tr>
          </thead>
          <tbody>
            {displayData.length > 0 ? (
              displayData.map((client, index) => {
                const activeIBs = getActiveIBs(client.ibs);
                
                return (
                  <tr key={index}>
                    <td className="referral-name-cell">
                      <strong>{client.client_name}</strong>
                    </td>
                    <td className="referral-email-cell">
                      {client.client_email}
                    </td>
                    <td className="referral-trade-ids-cell">
                      {client.trade_ids.map((id, idx) => (
                        <span key={idx} className="referral-trade-id-badge">
                          {id}
                        </span>
                      ))}
                    </td>
                    <td className="referral-parent-cell">
                      {client.parent ? (
                        <span className="referral-parent-email">{client.parent.email}</span>
                      ) : (
                        <span className="referral-root-badge">Root Client</span>
                      )}
                    </td>
                    <td className="referral-status-cell">
                      {client.parent ? (
                        <span className="referral-status-badge child">Child</span>
                      ) : (
                        <span className="referral-status-badge root">Root</span>
                      )}
                    </td>
                    <td className="referral-ib-cell">
                      {activeIBs.length > 0 ? (
                        <div className="referral-ib-chain-table">
                          {activeIBs.map((ib, idx) => (
                            <span key={idx} className="referral-ib-item">
                              IB{ib.level}: {ib.email}
                              {idx < activeIBs.length - 1 && ' → '}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="referral-no-ib">No IB Hierarchy</span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="referral-no-data-row">
                <td colSpan="6" className="referral-no-data-content">
                  <div className="referral-no-data-icon">📊</div>
                  <h3>No referral data found</h3>
                  <p>Try adjusting your search criteria or refresh the data.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="referral-tree-container">
      {/* Header Section */}
      <div className="header-section">
        <h1>Referral Tree</h1>
        <div className="breadcrumb">
          Home / <span>View All</span>
        </div>
      </div>

      {/* Stats Section */}
      <div className="referral-stats-section">
        <div className="referral-stat-card">
          <div className="referral-stat-number">{totalClients}</div>
          <div className="referral-stat-label">Total Clients</div>
        </div>
        <div className="referral-stat-card">
          <div className="referral-stat-number">{filteredData.length}</div>
          <div className="referral-stat-label">Filtered Results</div>
        </div>
        <div className="referral-stat-card">
          <div className="referral-stat-number">{filteredData.filter(c => c.parent).length}</div>
          <div className="referral-stat-label">Child Clients</div>
        </div>
        <div className="referral-stat-card">
          <div className="referral-stat-number">{filteredData.filter(c => !c.parent).length}</div>
          <div className="referral-stat-label">Root Clients</div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="referral-controls-section">
        <div className="referral-controls-left">
          <div className="referral-entries-control">
            <span>Show</span>
            <select value={entries} onChange={(e) => setEntries(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
        </div>
        
        <div className="referral-controls-right">
          <div className="referral-search-control">
            <span>Search:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email..."
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="referral-error-message">
          <span className="referral-error-icon">⚠️</span>
          {error}
          <button className="referral-retry-btn" onClick={fetchReferralData}>
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="referral-loading-container">
          <div className="referral-loading-spinner"></div>
          <p>Loading referral tree data...</p>
        </div>
      )}

      {/* Content Section */}
      {!loading && !error && (
        <>
          {/* Buttons Section */}
          <div className="referral-buttons-section">
            <button className="referral-print-btn">Print</button>
            <button className="referral-column-visibility-btn">Column visibility</button>
            <button className="referral-export-btn">Export Data</button>
          </div>

          {/* Referral Tree Table */}
          {renderReferralTable()}

          {/* Pagination Info */}
          {filteredData.length > entries && (
            <div className="referral-pagination-info">
              Showing {Math.min(entries, filteredData.length)} of {filteredData.length} entries
            </div>
          )}
        </>
      )}

      <footer className="referral-footer">
        <p>
          Copyright © 2025 <span className="referral-brand">Ample Markets</span>. All
          rights reserved.
        </p>
        <span className="referral-version">Version 2.0</span>
      </footer>
    </div>
  );
};

export default ReferralTree;
